import pako from 'pako'
import { until, useEventBus } from '@vueuse/core'
import { path, uniqBy } from 'ramda'
import useWebsocket from '../useWebsocket'
import useWidgetViewManager from '@/composables/widget-view-manager/useWidgetViewManager'
import { useWorkflow } from '@/composables/workflow/useWorkflow'
import { useI18n } from '@/composables/i18n/useI18n'
import type { LayoutDescriptor, Message } from '../frames'
import type { CustomPageLayout } from '@/views/apps/_customPage/types'
import useWidgetSession from '../../useWidgetSession'
import type { SessionContext, ViewRef, WorkflowEndReason } from '../../types'
import type { ViewState } from '../../view-state'
import { websocketFirstViewState, websocketNextViewState, websocketViewState } from './view-state'

const MAX_CHUNK_CHECKS = 50

let unwatchWorkflowEnd: (() => void) | undefined

export const currentTaskEvents = (): Message[] => {
    const { store } = useWidgetSession().transport
    const { activeMessage } = useWidgetViewManager()

    return store.tasks.value.filter(task => task.taskId === activeMessage.value?.taskId)
}

export const latestTaskEvent = (): Message | undefined => useWidgetSession().transport.store.latestTask.value

export const watchWorkflowEnd = (handler: (reason: WorkflowEndReason) => void) => {
    unwatchWorkflowEnd?.()

    unwatchWorkflowEnd = useEventBus<Message>('websocket-message').on(message => {
        if (message.workflowStatus === 'TIMEOUT') {
            handler('timeout')
        }

        if (message.workflowStatus === 'CANCELLED') {
            handler('cancelled')
        }
    })

    return unwatchWorkflowEnd
}

export const awaitFirstView = async (): Promise<ViewState | undefined> => {
    const { firstMessage } = useWebsocket()

    await until(firstMessage)
        .toBeTruthy({ timeout: 600 * 1000, throwOnTimeout: true })

    return firstMessage.value && websocketFirstViewState(firstMessage.value)
}

export const advance = async (_ctx: SessionContext, current: ViewRef): Promise<ViewState> => {
    return websocketNextViewState(await useWebsocket().waitForNewMessage(current.id))
}

export const changeLanguage = async (ctx: SessionContext, lang: string) => {
    const { waitForNewMessageByCount, replaceMessagesByTaskId } = useWebsocket()
    const { activeMessage, getAppViewMessages } = useWidgetViewManager()

    const taskId = activeMessage.value!.taskId
    const previousCount = getAppViewMessages.value.length

    await useWorkflow().resumeWorkflow(taskId, activeMessage.value!.taskType, lang)

    const newMessage = await waitForNewMessageByCount(taskId, previousCount)
    replaceMessagesByTaskId(taskId, previousCount)

    return websocketViewState(newMessage)
}

const layoutChunks = () => uniqBy(
    (m: Message) => path(['layoutDescriptor', 'index'], m),
    useWidgetViewManager().getAppViewMessages.value,
)

const awaitAllChunks = async (checks = 0): Promise<boolean> => {
    const { showError } = useWidgetViewManager()
    const { t } = useI18n()

    const messages = layoutChunks()

    if (checks >= MAX_CHUNK_CHECKS /*(5 seconds)*/) {
        showError({
            title: t('views.widget.incomplete_chunks.title'),
            description: t('views.widget.incomplete_chunks.description'),
            retry: true,
        })

        return false
    }

    if (typeof messages[0]?.layoutDescriptor === 'object') {
        const total = messages[0].layoutDescriptor.total
        const allFetched = messages
            .filter(m => !!path(['layoutDescriptor', 'chunk'], m))
            .length === total

        if (!allFetched) {
            await new Promise(resolve => setTimeout(resolve, 100))
            await awaitAllChunks(checks + 1)
        }

        return true
    }

    return true
}

export const resolveLayout = async (): Promise<CustomPageLayout> => {
    await awaitAllChunks()

    const baseDescriptor = layoutChunks()
        .map(m => m.layoutDescriptor as LayoutDescriptor)
        .sort((a, b) => a.index < b.index ? -1 : 1)
        .reduce((res, ld) => res += ld.chunk, '')

    const gzipped = Uint8Array.from(atob(baseDescriptor), char => char.charCodeAt(0))
    const layoutString = pako.inflate(gzipped, { to: 'string' })

    return JSON.parse(layoutString) as CustomPageLayout
}

export const resetRun = () => {
    unwatchWorkflowEnd?.()
    unwatchWorkflowEnd = undefined

    useWidgetViewManager().resetView()

    useWidgetSession().transport.reset()
}

export const disposeSession = () => {
    resetRun()
    useWorkflow().clearToken()
}
